import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box, Paper, Typography, TextField, Button, IconButton,
  Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Tooltip, Chip, FormControl, InputLabel, Select, MenuItem,
  Stack
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import GetAppIcon from "@mui/icons-material/GetApp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CancelIcon from "@mui/icons-material/Cancel";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import GavelIcon from "@mui/icons-material/Gavel";
import ReplayIcon from "@mui/icons-material/Replay";
import DeleteIcon from "@mui/icons-material/Delete";
import Euro from "@mui/icons-material/Euro";

const API = process.env.REACT_APP_API_URL;
const headersAuth = () => {
  const token = localStorage.getItem("token");
  return { Authorization: token ? `Bearer ${token}` : undefined, "Content-Type": "application/json" };
};

const colegiadoNombre = (i) => {
  const c = i?.colegiado || {};
  const nombre = c?.nombreCompleto || [c?.nombre, c?.apellido_1, c?.apellido_2].filter(Boolean).join(" ");
  return nombre || i?.colegiadoNombre || "-";
};
const colegiadoCodigo = (i) => i?.colegiado?.codigo ?? i?.colegiadoCodigo ?? i?.colegiadoId ?? "-";
const cursoNombre = (i, cursos) => {
  if (i?.curso?.nombre) return i.curso.nombre;
  const cid = i?.cursoId || i?.curso?.id;
  const c = cursos.find((x) => String(x.id) === String(cid));
  return c?.nombre ?? cid ?? "-";
};
const colegiadoEmail = (i) => {
  const c = i?.colegiado || {};
  return c?.email || i?.colegiadoEmail || i?.email || "-";
};
const colegiadoTelefonos = (i) => {
  const c = i?.colegiado || {};
  const t1 = c?.telefono_1 || c?.telefono1 || i?.telefono_1 || i?.telefono1 || "";
  const t2 = c?.telefono_2 || c?.telefono2 || i?.telefono_2 || i?.telefono2 || "";

  return {
    telefono1: t1 || "-",
    telefono2: t2 || "-",
  };
};

const colegiadoTelefono1 = (i) => {
  const c = i?.colegiado || {};
  return c?.telefono_1 || c?.telefono1 || i?.telefono_1 || i?.telefono1 || "-";
};

const colegiadoTelefono2 = (i) => {
  const c = i?.colegiado || {};
  return c?.telefono_2 || c?.telefono2 || i?.telefono_2 || i?.telefono2 || "-";
};

// Estados soportados (incluye REEMBOLSADA)
const ESTADOS = ["PENDIENTE", "PAGADA", "CANCELADA", "FINALIZADA", "PENALIZADA", "REEMBOLSADA"];

// Mapeo visual para Chip (label, color, icon)
const estadoMeta = (estadoCrudo, fallbackPagado) => {
  const e0 = (estadoCrudo || "").toString().toUpperCase();
  const estado = e0 || (fallbackPagado ? "PAGADA" : "PENDIENTE");

  switch (estado) {
    case "ACEPTADA":
      return { label: "Aceptada", color: "success", Icon: CheckCircleIcon };
    case "PAGADA":
      return { label: "Pagada", color: "success", Icon: Euro };
    case "CANCELADA":
      return { label: "Cancelada", color: "error", Icon: CancelIcon };
    case "FINALIZADA":
      return { label: "Finalizada", color: "info", Icon: DoneAllIcon };
    case "PENALIZADA":
      return { label: "Penalizada", color: "warning", Icon: GavelIcon };
    case "REEMBOLSADA":
      return { label: "Reembolsada", color: "secondary", Icon: ReplayIcon };
    case "PENDIENTE":
    default:
      return { label: "Pendiente", color: "default", Icon: PendingActionsIcon };
  }
};

export default function InscripcionesAdmin({ cursoId: cursoIdProp = null }) {
  const [cursos, setCursos] = useState([]);
  const [cursoId, setCursoId] = useState(cursoIdProp);
  const [soloPagadas, setSoloPagadas] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleExportTabla = () => {
    if (!filtered.length) {
      alert("No hay filas para exportar.");
      return;
    }

    const header = [
      "ID",
      "Colegiado",
      "Código",
      "Email",
      "Teléfono 1",
      "Teléfono 2",
      "Curso",
      "Estado",
      "Fecha"
    ];

    const lines = filtered.map((i) => {
      const meta = estadoMeta(i?.estado, i?.pagado === true);
      const fecha = i?.fechaInscripcion || i?.fecha || i?.createdAt || i?.creado_el || "";

      const { telefono1, telefono2 } = colegiadoTelefonos(i);

      const vals = [
        i.id ?? "",
        colegiadoNombre(i) ?? "",
        colegiadoCodigo(i) ?? "",
        colegiadoEmail(i) ?? "",
        telefono1,
        telefono2,
        cursoNombre(i, cursos) ?? "",
        meta.label,
        fecha,
      ];

      return vals.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";");
    });

    const csv = "\uFEFF" + [header.join(";"), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `inscripciones_curso_${cursoId || "todos"}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(url);
  };

  const loadCursos = async () => {
    try {
      const { data } = await axios.get(`${API}/api/cursos`, { headers: headersAuth() });
      setCursos(data || []);
      if (!cursoIdProp && data?.length && !cursoId) setCursoId(data[0].id);
    } catch (e) {
      console.error(e);
      alert("No se pudieron cargar los cursos.");
    }
  };

  const loadInscripciones = async (id, onlyPaid) => {
    if (!id) return;
    setLoading(true);
    try {
      const url = onlyPaid
        ? `${API}/inscripciones/curso/${id}/pagadas`
        : `${API}/inscripciones/curso/${id}`;
      const { data } = await axios.get(url, { headers: headersAuth() });
      setRows(Array.isArray(data) ? data : (data ? [data] : []));
      setPage(0);
    } catch (e) {
      console.error(e);
      alert("No se pudieron cargar las inscripciones.");
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => loadInscripciones(cursoId, soloPagadas);

  // Acciones: endpoint genérico /{id}/estado
  const setEstado = async (ins, nuevoEstado) => {
    try {
      await axios.put(
        `${API}/inscripciones/${ins.id}/estado`,
        { estado: nuevoEstado },
        { headers: headersAuth() }
      );
      setRows((prev) =>
        prev.map((r) => (r.id === ins.id ? { ...r, estado: nuevoEstado, pagado: nuevoEstado === "PAGADA" } : r))
      );
    } catch (e) {
      console.error(e);
      alert(`No se pudo cambiar el estado a ${nuevoEstado}.`);
    }
  };

  const eliminar = async (ins) => {
    if (!window.confirm(`¿Eliminar inscripción #${ins.id} de colegiado ${colegiadoCodigo(ins)}?`)) return;
    try {
      await axios.delete(`${API}/inscripciones/${ins.id}`, { headers: headersAuth() });
      setRows((prev) => prev.filter((r) => r.id !== ins.id));
    } catch (e) {
      console.error(e);
      alert("No se pudo eliminar la inscripción.");
    }
  };

  useEffect(() => { loadCursos(); }, []);
  useEffect(() => { if (cursoId) refresh(); }, [cursoId, soloPagadas]);

  const filtered = useMemo(() => {
    const term = (busqueda || "").toLowerCase();
    if (!term) return rows;
    return rows.filter((i) => {
      const nom = (colegiadoNombre(i) || "").toLowerCase();
      const cod = String(colegiadoCodigo(i) || "").toLowerCase();
      return nom.includes(term) || cod.includes(term);
    });
  }, [rows, busqueda]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  // Métricas por estado
  const estadoDe = (r) => (r?.estado || (r?.pagado ? "PAGADA" : "PENDIENTE")).toUpperCase();
  const countByEstado = (estado) => rows.filter((r) => estadoDe(r) === estado).length;

  const total = rows.length;
  const totalPend = countByEstado("PENDIENTE");
  const totalOk = countByEstado("ACEPTADA");
  const totalPag = countByEstado("PAGADA");
  const totalCan = countByEstado("CANCELADA");
  const totalFin = countByEstado("FINALIZADA");
  const totalPen = countByEstado("PENALIZADA");
  const totalRee = countByEstado("REEMBOLSADA");

  const selectedCurso = useMemo(
    () => cursos.find((c) => String(c.id) === String(cursoId)),
    [cursos, cursoId]
  );
  const tituloNombre = selectedCurso?.nombre ?? (cursoId ? String(cursoId) : "—");
  const topeAlumnos = selectedCurso?.tope_alumnos ?? selectedCurso?.topeAlumnos ?? null;
  const alumnosMostrados = filtered.length;

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          {tituloNombre} — Tope: {topeAlumnos ?? "-"} · Mostrados: {alumnosMostrados}
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <FormControl fullWidth>
              <InputLabel id="curso-label">Curso</InputLabel>
              <Select
                labelId="curso-label"
                label="Curso"
                value={cursoId ?? ""}
                onChange={(e) => setCursoId(e.target.value)}
              >
                {cursos.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.nombre}{" "}
                    {c.fechaDesde ? ` (${c.fechaDesde}${c.fechaHasta ? " → " + c.fechaHasta : ""})` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="Buscar colegiado (nombre/código)"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel id="pagadas-label">Mostrar</InputLabel>
              <Select
                labelId="pagadas-label"
                label="Mostrar"
                value={soloPagadas ? "pagadas" : "todas"}
                onChange={(e) => setSoloPagadas(e.target.value === "pagadas")}
              >
                <MenuItem value="todas">Todas</MenuItem>
                <MenuItem value="pagadas">Solo pagadas</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2} display="flex" gap={1} justifyContent="flex-end">
            <Button startIcon={<RefreshIcon />} onClick={refresh} variant="outlined">Actualizar</Button>
            <Button startIcon={<GetAppIcon />} onClick={handleExportTabla} variant="outlined">
              Exportar tabla
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label={`Total: ${total}`} />
              <Chip icon={<PendingActionsIcon />} label={`Pendientes: ${totalPend}`} variant="outlined" />
              <Chip icon={<CheckCircleIcon />} label={`Aceptadas: ${totalOk}`} color="success" variant="outlined" />
              <Chip icon={<Euro />} label={`Pagadas: ${totalPag}`} color="success" variant="outlined" />
              <Chip icon={<CancelIcon />} label={`Canceladas: ${totalCan}`} color="error" variant="outlined" />
              <Chip icon={<DoneAllIcon />} label={`Finalizadas: ${totalFin}`} color="info" variant="outlined" />
              <Chip icon={<GavelIcon />} label={`Penalizadas: ${totalPen}`} color="warning" variant="outlined" />
              <Chip icon={<ReplayIcon />} label={`Reembolsadas: ${totalRee}`} color="secondary" variant="outlined" />
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Colegiado</TableCell>
                <TableCell>Código</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Teléfono 1</TableCell>
                <TableCell>Teléfono 2</TableCell>
                <TableCell>Curso</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((i) => {
                const fecha = i?.fechaInscripcion || i?.fecha || i?.createdAt || i?.creado_el || "-";
                const meta = estadoMeta(i?.estado, i?.pagado === true);

                return (
                  <TableRow key={i.id}>
                    <TableCell>{i.id}</TableCell>
                    <TableCell>{colegiadoNombre(i)}</TableCell>
                    <TableCell>{colegiadoCodigo(i)}</TableCell>
                    <TableCell>{colegiadoEmail(i)}</TableCell>
                    <TableCell>{colegiadoTelefono1(i)}</TableCell>
                    <TableCell>{colegiadoTelefono2(i)}</TableCell>
                    <TableCell>{cursoNombre(i, cursos)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={<meta.Icon fontSize="small" />}
                        label={meta.label}
                        color={meta.color}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{fecha}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="PENDIENTE">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => setEstado(i, "PENDIENTE")}
                            disabled={estadoDe(i) === "PENDIENTE"}
                          >
                            <PendingActionsIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="ACEPTADA">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => setEstado(i, "ACEPTADA")}
                            disabled={estadoDe(i) === "ACEPTADA"}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="PAGADA">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => setEstado(i, "PAGADA")}
                            disabled={estadoDe(i) === "PAGADA"}
                          >
                            <Euro fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="CANCELADA">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => setEstado(i, "CANCELADA")}
                            disabled={estadoDe(i) === "CANCELADA"}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="FINALIZADA">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => setEstado(i, "FINALIZADA")}
                            disabled={estadoDe(i) === "FINALIZADA"}
                          >
                            <DoneAllIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="PENALIZADA">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => setEstado(i, "PENALIZADA")}
                            disabled={estadoDe(i) === "PENALIZADA"}
                          >
                            <GavelIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="REEMBOLSADA">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => setEstado(i, "REEMBOLSADA")}
                            disabled={estadoDe(i) === "REEMBOLSADA"}
                          >
                            <ReplayIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="Eliminar">
                        <IconButton size="small" color="error" onClick={() => eliminar(i)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}

              {paginated.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      {cursoId ? "No hay inscripciones para este curso." : "Selecciona un curso para listar inscripciones."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>
    </Box>
  );
}
